import React, { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCommentDots, faPaperPlane, faMinus, faChevronUp } from '@fortawesome/free-solid-svg-icons'
import { useSettings } from '../contexts/SettingsContext'
import useMediaQuery from '../hooks/useMediaQuery'

const AIAssistantModal: React.FC = () => {
  const { aiAssistant } = useSettings()
  const isDesktop = useMediaQuery('(min-width: 768px)')

  const [message, setMessage] = useState('')
  const [isMinimized, setIsMinimized] = useState(true) // Start minimized by default
  const [messages, setMessages] = useState<Array<{ id: string; text: string; isUser: boolean; timestamp: Date }>>([
    {
      id: '1',
      text: 'Hello! I\'m your AI assistant. How can I help you today?',
      isUser: false,
      timestamp: new Date()
    }
  ])

  const handleSendMessage = () => {
    if (!message.trim()) return

    const userMessage = {
      id: Date.now().toString(),
      text: message,
      isUser: true,
      timestamp: new Date()
    }

    setMessages(prev => [...prev, userMessage])
    setMessage('')

    // Simulate AI response
    setTimeout(() => {
      const aiResponse = {
        id: (Date.now() + 1).toString(),
        text: 'I understand you said: "' + userMessage.text + '". This is a demo response. In a real implementation, this would be connected to an AI service.',
        isUser: false,
        timestamp: new Date()
      }
      setMessages(prev => [...prev, aiResponse])
    }, 1000)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  if (!aiAssistant) return null

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 flex justify-start pb-safe">
      {/* Main Dock Container */}
      <div className={`bg-base-100 border-t border-r border-base-300 shadow-lg transition-all duration-300 ease-in-out ${
        isMinimized ? 'h-16' : 'h-96 sm:h-[500px]'
      } w-full ${isDesktop ? 'max-w-2xl ml-4' : ''} rounded-t-lg`}>
        
        {/* Header */}
        <div className="navbar bg-base-200 border-b border-base-300 rounded-t-lg">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faCommentDots} className="text-primary text-lg" />
              <span className="font-semibold text-base-content">AI Assistant</span>
              {messages.length > 1 && (
                <div className="badge badge-primary badge-sm">{messages.length - 1}</div>
              )}
            </div>
          </div>
          <div className="flex-none">
            <button
              className="btn btn-ghost btn-sm btn-square"
              onClick={() => setIsMinimized(!isMinimized)}
              aria-label={isMinimized ? "Expand AI Assistant" : "Minimize AI Assistant"}
            >
              <FontAwesomeIcon icon={isMinimized ? faChevronUp : faMinus} />
            </button>
          </div>
        </div>

        {/* Chat Content - only show when not minimized */}
        {!isMinimized && (
          <div className="flex flex-col" style={{ height: 'calc(100% - 64px)' }}>
            {/* Messages Area */}
            <div className="overflow-y-auto p-4 space-y-4" style={{ height: 'calc(100% - 80px)' }}>
              <div className="flex flex-col space-y-4">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`chat ${msg.isUser ? 'chat-end' : 'chat-start'}`}
                  >
                    <div className="chat-bubble">
                      <p className="text-sm">{msg.text}</p>
                      <time className="text-xs opacity-70">
                        {msg.timestamp.toLocaleTimeString()}
                      </time>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Input Area */}
            <div className="border-t border-base-300 p-4" style={{ height: '80px' }}>
              <div className="join w-full">
                <input
                  type="text"
                  placeholder="Type your message..."
                  className="input input-bordered join-item flex-1"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
                <button
                  className="btn btn-primary join-item"
                  onClick={handleSendMessage}
                  disabled={!message.trim()}
                >
                  <FontAwesomeIcon icon={faPaperPlane} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AIAssistantModal
